/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { Bed, ChevronRight, LayoutGrid, List, Loader2, Users } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import Footer from '@/components/footer'
import Header from '@/components/Header'
import { Button } from '@/components/ui/button'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useGetAllRooms, useGetAvailableRooms, useGetGuestHouseImages } from '@/utils/api'

// Separate component to handle image fetching for a single guest house
function GuestHouseImageFetcher({
  guestHouseId,
  onImagesLoaded,
}: {
  guestHouseId: number
  onImagesLoaded: (id: number, images: string[]) => void
}) {
  const { data: imagesResponse } = useGetGuestHouseImages(guestHouseId, {
    page: 0,
    size: 50,
  })

  useEffect(() => {
    if (imagesResponse?.data?.content) {
      const imageUrls = imagesResponse.data.content
        .map(img => img.imageUrl)
        .filter((url): url is string => typeof url === 'string')
      onImagesLoaded(guestHouseId, imageUrls)
    }
  }, [imagesResponse, guestHouseId, onImagesLoaded])

  return null // This component doesn't render anything
}

// Helper function to format dates with month names
function formatDateWithMonthName(dateString: string): string {
  if (!dateString || dateString.trim() === '')
    return ''

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (Number.isNaN(date.getTime()))
      return dateString

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }

    return date.toLocaleDateString('en-US', options)
  }
  catch {
    // If parsing fails, return original string
    return dateString
  }
}

// Helper function to calculate nights between two dates
function calculateNights(checkInDate: string, checkOutDate: string): number {
  if (!checkInDate || !checkOutDate || checkInDate.trim() === '' || checkOutDate.trim() === '') {
    return 0
  }

  try {
    const checkIn = new Date(checkInDate)
    const checkOut = new Date(checkOutDate)

    // Check if dates are valid
    if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime())) {
      return 0
    }

    // Calculate difference in milliseconds and convert to days
    const timeDifference = checkOut.getTime() - checkIn.getTime()
    const nights = Math.ceil(timeDifference / (1000 * 3600 * 24))

    return Math.max(0, nights) // Ensure non-negative result
  }
  catch {
    return 0
  }
}

export const Route = createFileRoute('/_landing/rooms/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      checkIn: (search.checkIn as string) || '',
      checkOut: (search.checkOut as string) || '',
      adults: Number(search.adults) || 1,
      children: Number(search.children) || 0,
      page: Number(search.page) || 0,
    }
  },
})

function RouteComponent() {
  const searchParams = useSearch({ from: '/_landing/rooms/' })
  const [currentPage, setCurrentPage] = useState(searchParams.page || 0)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [guestHouseImages, setGuestHouseImages] = useState<Record<number, string[]>>({})
  const [roomsWithImages, setRoomsWithImages] = useState<any[]>([])

  // Only consider it a date filter if both dates are provided and not empty
  const hasDateFilter = !!(
    searchParams.checkIn
    && searchParams.checkOut
    && searchParams.checkIn.trim() !== ''
    && searchParams.checkOut.trim() !== ''
  )

  // Fetch available rooms based on search parameters (when dates are provided)
  const {
    data: availableRoomsResponse,
    isLoading: isLoadingAvailable,
    error: availableError,
  } = useGetAvailableRooms(
    {
      checkInDate: searchParams.checkIn,
      checkOutDate: searchParams.checkOut,
    },
    {
      query: {
        enabled: hasDateFilter,
      },
    },
  )

  // Fetch all rooms (when no dates are provided or dates are empty)
  const {
    data: allRoomsResponse,
    isLoading: isLoadingAll,
    error: allRoomsError,
  } = useGetAllRooms(
    {
      page: currentPage,
      size: 12,
    },
    {
      query: {
        enabled: !hasDateFilter,
      },
    },
  )

  // Determine which data to use
  const roomsData = hasDateFilter ? availableRoomsResponse?.data : allRoomsResponse?.data?.content
  const isLoading = hasDateFilter ? isLoadingAvailable : isLoadingAll
  const error = hasDateFilter ? availableError : allRoomsError
  const totalPages = hasDateFilter ? 1 : allRoomsResponse?.data?.totalPages || 1

  // Get unique guest house IDs from the rooms
  const guestHouseIds: number[] = roomsData
    ? Array.from(
        new Set(
          roomsData
            .map(room => room.guestHouseId)
            .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id)),
        ),
      )
    : []

  const handleImagesLoaded = useCallback((id: number, images: string[]) => {
    setGuestHouseImages((prev) => {
      // Only update if we have new images or different images
      if (!prev[id] || prev[id].length !== images.length || !prev[id].every((img, idx) => img === images[idx])) {
        return { ...prev, [id]: images }
      }
      return prev
    })
  }, [])

  // Update rooms with images when images are loaded
  useEffect(() => {
    if (roomsData && roomsData.length > 0) {
      const updatedRooms = roomsData.map((room) => {
        const guestHouseId = room.guestHouseId
        let roomImage = ''

        if (guestHouseId && guestHouseImages[guestHouseId]) {
          const images = guestHouseImages[guestHouseId]
          if (images.length > 0) {
            // Find index of this room among rooms with the same guestHouseId
            const roomsWithSameGuestHouse = roomsData.filter(r => r.guestHouseId === guestHouseId)
            const indexInGuestHouse = roomsWithSameGuestHouse.findIndex(r => r.id === room.id)
            roomImage = images[indexInGuestHouse % images.length]
          }
        }

        return {
          ...room,
          image: roomImage,
        }
      })

      setRoomsWithImages(updatedRooms)
    }
    else if (roomsData) {
      // Set rooms with empty images initially
      const roomsWithPlaceholders = roomsData.map(room => ({
        ...room,
        image: '',
      }))
      setRoomsWithImages(roomsWithPlaceholders)
    }
  }, [roomsData, guestHouseImages])

  // Format currency
  const formatCurrency = (amount: number, currencyCode = 'UGX') => {
    if (currencyCode === 'UGX') {
      return new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <>
      <Header />
      {/* Render image fetchers for each guest house */}
      {guestHouseIds.map(id => (
        <GuestHouseImageFetcher key={id} guestHouseId={id} onImagesLoaded={handleImagesLoaded} />
      ))}

      {/* Page Banner */}
      <section className="relative h-[300px]">
        <img
          style={{ height: 300, width: 2500 }}
          src="/room1.jpg"
          loading="lazy"
          alt=""
          className="object-cover blur-xs scale-100 w-full h-full absolute inset-0"
        />
        <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-white font-bold">
          <h1 className="text-4xl font-bold mb-4">
            <span className="text-primary">
              {hasDateFilter ? 'Available' : 'Our'}
              {' '}
            </span>
            <span className="text-primary">Rooms</span>
          </h1>
          <div className="flex items-center gap-2">
            <Link href="/" className="hover:text-orange-500 transition-colors" to="/">
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span>{hasDateFilter ? 'Available Rooms' : 'Rooms & Suites'}</span>
          </div>
          {hasDateFilter && (
            <div className="mt-4 text-sm text-center">
              <div className="font-medium">
                {formatDateWithMonthName(searchParams.checkIn)}
                {' '}
                to
                {formatDateWithMonthName(searchParams.checkOut)}
              </div>
              <div className="mt-1 text-xs opacity-90">
                {searchParams.adults}
                {' '}
                Adult
                {searchParams.adults !== 1 ? 's' : ''}
                {' '}
                •
                {' '}
                {calculateNights(searchParams.checkIn, searchParams.checkOut)}
                {' '}
                Night
                {calculateNights(searchParams.checkIn, searchParams.checkOut) !== 1 ? 's' : ''}
                {searchParams.children > 0
                  && ` • ${searchParams.children} Child${searchParams.children !== 1 ? 'ren' : ''}`}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Rooms Grid */}
      <section className="container mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-6">
          <div className="text-sm text-gray-600">
            {isLoading
              ? hasDateFilter
                ? 'Searching for available rooms...'
                : 'Loading rooms...'
              : roomsWithImages.length > 0
                ? hasDateFilter
                  ? `${roomsWithImages.length} rooms available`
                  : `${roomsWithImages.length} rooms total`
                : 'No rooms found'}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setViewMode('grid')} variant={viewMode === 'grid' ? 'default' : 'outline'} size="sm">
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button onClick={() => setViewMode('list')} variant={viewMode === 'list' ? 'default' : 'outline'} size="sm">
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-gray-600">
              {hasDateFilter ? 'Searching for available rooms...' : 'Loading rooms...'}
            </span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-red-600 mb-2">Error loading rooms</h3>
            <p className="text-gray-600">Please try again later or modify your search criteria.</p>
          </div>
        )}

        {/* Rooms Grid/List */}
        {roomsWithImages.length > 0 && (
          <div
            className={`gap-6 ${
              viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'flex flex-col space-y-4'
            }`}
          >
            {roomsWithImages.map((room) => {
              // Get price from rates
              const price = room.rates?.[0]?.amount || 100000
              const currency = room.rates?.[0]?.currency || 'UGX'

              if (viewMode === 'list') {
                return (
                  <div
                    key={room.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow border border-gray-200"
                  >
                    <div className="flex h-32">
                      {/* Compact Image Section */}
                      <div className="relative w-48 flex-shrink-0">
                        <img
                          src={room.image}
                          alt={`${room.guestHouse} - ${room.roomNumber}`}
                          className="object-cover w-full h-full"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                          }}
                        />
                        <div className="absolute top-2 left-2">
                          <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
                            {room.guestHouse}
                          </span>
                        </div>
                      </div>

                      {/* Compact Content Section */}
                      <div className="flex-1 p-4 flex justify-between">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-lg font-semibold text-blue-600 mb-1">
                                {room.roomNumber}
                                HELLO
                              </h3>
                              <p className="text-sm text-gray-600 mb-1">{room.guestHouse}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Bed className="h-4 w-4" />
                                  <span>{room.bedType?.replace(/_/g, ' ') || 'Standard'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  <span>
                                    Max
                                    {room.maxOccupancy}
                                    {' '}
                                    guests
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Price and Action Section */}
                        <div className="flex flex-col items-end justify-between ml-4">
                          <div className="text-right mb-2">
                            <div className="text-2xl font-bold text-primary">{formatCurrency(price, currency)}</div>
                            <p className="text-xs text-gray-500">per night</p>
                          </div>
                          <Link
                            to="/rooms/$roomId"
                            params={{ roomId: room.id?.toString() ?? '' }}
                            search={{
                              checkIn: searchParams.checkIn,
                              checkOut: searchParams.checkOut,
                              adults: searchParams.adults,
                              children: searchParams.children,
                            }}
                          >
                            <Button className="bg-primary hover:bg-pink-600 text-white text-sm cursor-pointer px-6">
                              {hasDateFilter ? 'Book Now' : 'View Details'}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              // Grid view (existing code)
              return (
                <div
                  key={room.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="relative h-64">
                    <img
                      src={room.image}
                      alt={`${room.guestHouse} - ${room.roomNumber}`}
                      className="object-cover w-full h-full"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
                        {room.guestHouse}
                      </span>
                    </div>

                    {/* Show loading indicator for images */}
                    {guestHouseIds.length > 0 && Object.keys(guestHouseImages).length < guestHouseIds.length && (
                      <div className="absolute bottom-4 right-4">
                        <div className="bg-white/80 rounded px-2 py-1 text-xs text-gray-600">Loading images...</div>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-blue-600">{room.roomNumber}</h3>
                        <p className="text-sm text-gray-500">{room.guestHouse}</p>
                        <p className="text-xs text-gray-400">{room.bedType?.replace(/_/g, ' ')}</p>
                      </div>
                      <div className="text-right">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                          {formatCurrency(price, currency)}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">per night</p>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4">
                      Max Occupancy:
                      {room.maxOccupancy}
                      {' '}
                      Guests
                    </p>

                    <div className="flex justify-between items-center">
                      <Link
                        to="/rooms/$roomId"
                        params={{ roomId: room.id?.toString() ?? '' }}
                        search={{
                          checkIn: searchParams.checkIn,
                          checkOut: searchParams.checkOut,
                          adults: searchParams.adults,
                          children: searchParams.children,
                        }}
                      >
                        <Button className="bg-primary hover:bg-pink-600 text-white text-sm cursor-pointer">
                          {hasDateFilter ? 'Book Now' : 'View Details'}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* No Rooms Found */}
        {roomsWithImages.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              {hasDateFilter ? 'No rooms available' : 'No rooms found'}
            </h3>
            <p className="text-gray-600 mb-4">
              {hasDateFilter
                ? 'No rooms are available for your selected dates and guest count.'
                : 'No rooms have been added yet.'}
            </p>
            <Link to="/">
              <Button className="bg-primary hover:bg-pink-600 text-white">
                {hasDateFilter ? 'Modify Search' : 'Go to Homepage'}
              </Button>
            </Link>
          </div>
        )}

        {/* Pagination - only show for all rooms view */}
        {!hasDateFilter && roomsWithImages.length > 0 && totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 0))}
                    className={currentPage === 0 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNumber = currentPage < 3 ? i : currentPage - 2 + i
                  if (pageNumber >= totalPages)
                    return null
                  return (
                    <PaginationItem key={pageNumber}>
                      <PaginationLink
                        href="#"
                        isActive={currentPage === pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber + 1}
                      </PaginationLink>
                    </PaginationItem>
                  )
                })}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages - 1))}
                    className={currentPage >= totalPages - 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </section>

      <Footer />
    </>
  )
}
